/**
 * Centralized logging utility for the Patientree UI
 * Provides consistent logging across the application with different levels
 */

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  /**
   * Log info messages (only in development)
   * @param {string} message - The message to log
   * @param {any} data - Optional data to log
   */
  info(message, data = null) {
    if (this.isDevelopment) {
      if (data) {
        console.log(`[INFO] ${message}`, data);
      } else {
        console.log(`[INFO] ${message}`);
      }
    }
  }

  /**
   * Log warning messages
   * @param {string} message - The message to log
   * @param {any} data - Optional data to log
   */
  warn(message, data = null) {
    if (data) {
      console.warn(`[WARN] ${message}`, data);
    } else {
      console.warn(`[WARN] ${message}`);
    }
  }

  /**
   * Log error messages (always logged)
   * @param {string} message - The message to log
   * @param {Error|any} error - The error object or data
   */
  error(message, error = null) {
    if (error) {
      console.error(`[ERROR] ${message}`, error);
    } else {
      console.error(`[ERROR] ${message}`);
    }
  }

  /**
   * Log debug messages (only in development)
   * @param {string} message - The message to log
   * @param {any} data - Optional data to log
   */
  debug(message, data = null) {
    if (this.isDevelopment) {
      if (data) {
        console.log(`[DEBUG] ${message}`, data);
      } else {
        console.log(`[DEBUG] ${message}`);
      }
    }
  }

  /**
   * Log API calls (only in development)
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {any} data - Request/response data
   */
  api(method, url, data = null) {
    if (this.isDevelopment) {
      if (data) {
        console.log(`[API] ${method} ${url}`, data);
      } else {
        console.log(`[API] ${method} ${url}`);
      }
    }
  }

  /**
   * Log authentication events
   * @param {string} event - Auth event type
   * @param {any} data - Event data
   */
  auth(event, data = null) {
    if (this.isDevelopment) {
      if (data) {
        console.log(`[AUTH] ${event}`, data);
      } else {
        console.log(`[AUTH] ${event}`);
      }
    }
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
